/**
 * Proposal documents for an estimate's customer.
 *
 * `documents` has no estimate column; `proposal-pdf-generate` records the
 * PDF under a deterministic storage key (`proposals/<customer>/<number>.pdf`)
 * with category `proposal`, so we list the customer's proposals and the page
 * flags the rows that belong to the estimate it is showing (see
 * `withOwnDocumentFlag`). The query is cached per customer only, so the
 * per-estimate flag is deliberately not computed inside the query.
 */
import { useQuery } from '@tanstack/react-query';
import { db, describeDbError } from '../lib/db';
import { supabase } from '../lib/supabase';
import {
  PROPOSAL_DOCUMENT_COLUMNS,
  proposalPdfKey,
  type ProposalDocumentRow,
} from '../lib/estimateTypes';

export interface ProposalDocument extends ProposalDocumentRow {
  forThisEstimate: boolean;
}

export const proposalDocumentsQueryKey = (customerId: string | null | undefined) =>
  ['proposal-documents', customerId ?? null] as const;

export function useProposalDocuments(
  customerId: string | null | undefined,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: proposalDocumentsQueryKey(customerId),
    enabled: Boolean(customerId),
    queryFn: async (): Promise<ProposalDocumentRow[]> => {
      if (!customerId) return [];
      const { data, error } = await db
        .from('documents')
        .select(PROPOSAL_DOCUMENT_COLUMNS)
        .eq('customer_id', customerId)
        .eq('category', 'proposal')
        .order('updated_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(describeDbError(error, 'Could not load proposal documents.'));
      return (data ?? []) as ProposalDocumentRow[];
    },
    staleTime: 15_000,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

/** Flags the rows whose storage key is the one `proposal-pdf-generate` writes for this estimate. */
export function withOwnDocumentFlag(
  rows: ProposalDocumentRow[] | undefined,
  customerId: string | null | undefined,
  estimateNumber: string | null | undefined,
): ProposalDocument[] | undefined {
  if (!rows) return undefined;
  const ownKey = customerId && estimateNumber ? proposalPdfKey(customerId, estimateNumber) : null;
  return rows.map((row) => ({ ...row, forThisEstimate: ownKey !== null && row.s3_key === ownKey }));
}

/** Short-lived signed URL for a document in the private `documents` bucket. */
export async function createDocumentDownloadUrl(s3Key: string): Promise<string> {
  const { data, error } = await supabase.storage.from('documents').createSignedUrl(s3Key, 60);
  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'Could not create a download link for this document.');
  }
  return data.signedUrl;
}
